import { Check } from 'lucide-react'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { FeatureSectionData } from '@/types/features'
import SmartLinkShowcase from './SmartLinkShowcase'
import Showcase, { INTERACTIVE_FRAME_HEIGHT, isInteractive, type InteractiveScreen } from './showcase/Showcase'
import type { ShowcaseScreen, ShowcaseVariant } from './showcase/fixtures'

/**
 * How much of each screen the section's frame shows, in the dashboard's own
 * 1280px-wide pixels: down to the end of the part the section is about (the
 * builder's checks, the canvas, the smart link card), never mid-card.
 */
export const SECTION_FRAME_HEIGHT: Record<Exclude<ShowcaseScreen, InteractiveScreen>, number> = {
  home: 900,
  'release-builder': 920,
  catalog: 720,
  analytics: 900,
  statements: 840,
  artwork: 980,
  'smart-links': 1080,
  labels: 560,
  // Tall enough for the whole chat panel (its height is capped at 40rem).
  'live-chat': 920,
  // The studio dialog is 90% of the frame's height: enough for its rail and footer.
  lyrics: 920,
}

/** An interactive demo shows the whole screen; every other frame shows its section. */
export function frameHeight(screen: ShowcaseScreen): number {
  return isInteractive(screen) ? INTERACTIVE_FRAME_HEIGHT : SECTION_FRAME_HEIGHT[screen]
}

export default function FeatureSection({ section, index, variant = 'artist' }: {
  section: FeatureSectionData
  index: number
  /** The hub's Artist/Label view — the frame draws that workspace. */
  variant?: ShowcaseVariant
}) {
  const flip = index % 2 === 1
  return (
    <section id={section.key} className="marketing-section scroll-mt-24">
      <div className="container mx-auto px-6">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          {/* The order classes sit on ScrollReveal: its wrapper, not the inner div, is the grid item. */}
          <ScrollReveal direction={flip ? 'right' : 'left'} className={flip ? 'lg:order-2' : undefined}>
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="marketing-section-title">{section.title}</h2>
                {section.badge && <span className="inline-flex items-center whitespace-nowrap rounded-full border border-[#ff5400]/35 bg-[#ff5400]/[0.16] px-3 py-1 text-[0.72rem] font-extrabold uppercase tracking-[0.18em] text-white">{section.badge}</span>}
              </div>
              <p className="marketing-lead mt-4">{section.summary}</p>
              <ul className="mt-6 space-y-3">
                {section.points.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm leading-6 text-white/80">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-white/70" aria-hidden="true" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              {section.href && (
                <a href={section.href} className="marketing-button-secondary mt-6">
                  Learn more<span className="sr-only">: {section.title}</span> <span aria-hidden="true">→</span>
                </a>
              )}
            </div>
          </ScrollReveal>
          <ScrollReveal direction={flip ? 'left' : 'right'} className={flip ? 'lg:order-1' : undefined}>
            {/* A smart link is the page a fan opens, so it is drawn as one —
                an lnkb.ee page on a phone — not as a dashboard screen. */}
            {section.screen === 'smart-links' ? (
              <SmartLinkShowcase variant={variant} label={`${section.title}: a release smart link as a fan sees it, with sample data`} />
            ) : (
              <Showcase
                screen={section.screen}
                variant={variant}
                height={frameHeight(section.screen)}
                label={`${section.title}: the real wavbee dashboard screen, with sample data`}
              />
            )}
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
